import { Immutable } from "immer";
import React, { useEffect } from "react";

import { ProductCompareTheme } from "../../../constants";
import { ISafeHTML, WebPageURL } from "../../../models/nominals";
import {
    IAttributeOptionGroup,
    ICompareTile,
    IGroupColumnsHeader,
} from "../../../models/product-compare";
import { useAppDispatch, useAppSelector } from "../../../store/reducers";
import { trackCategoryPageView } from "../../../utils/analytics";
import { notEmpty } from "../../../utils/functional";
import { SelectedProductTray } from "../SelectedProductTray";
import { defaults } from "../defaults";
import { addToSelected, removeFromSelected, showModal } from "../reducers";
import { getSelectedVariant } from "../selectors";
import { ProductSelectionGrid as ProductSelectionGridComponent } from "./ProductSelectionGridContent";

type IProps = Immutable<{
    header: ISafeHTML;
    financingLink: WebPageURL;
    ctaText?: string;
    preselectedSize: string | null;
    productComparePageURL: WebPageURL | null;
    sizeAttributeOptionGroup: IAttributeOptionGroup | null;
    showStartingAt?: boolean;
    showRating?: boolean;
    theme: ProductCompareTheme;
    footer: ISafeHTML | null;
    groupHeader?: IGroupColumnsHeader | null;
    showSizeSelector?: boolean | null;
}>;

const trackCategoryView = (
    tiles: readonly ICompareTile[],
    priceSelectValue: string,
): void => {
    // Obtain SKUs for current variants
    const skus = tiles
        .map((tile) => {
            const variant = getSelectedVariant(tile, priceSelectValue);
            return variant ? variant.skus[0] : null;
        })
        .filter(notEmpty);
    trackCategoryPageView(skus);
};

const shouldShowCompareFunctionality = (
    theme: ProductCompareTheme,
): boolean => {
    return ![
        ProductCompareTheme.WITHOUT_COMPARE,
        ProductCompareTheme.THEME_ONE,
    ].includes(theme);
};

export const ProductSelectionGrid = (props: IProps): React.ReactNode => {
    const state = useAppSelector(
        (rootState) => rootState.productcompare || defaults,
    );
    const dispatch = useAppDispatch();

    const tiles = state.tiles;
    const selectedTileIDs = state.selectedTileIDs;
    const priceSelectValue = state.priceSelectValue;

    const hasRenderedOnceRef = React.useRef(false);
    useEffect(() => {
        if (!hasRenderedOnceRef.current) {
            hasRenderedOnceRef.current = true;
            return;
        }
        trackCategoryView(tiles, priceSelectValue);
    });

    const showCompareFunctionality = shouldShowCompareFunctionality(
        props.theme,
    );

    const handleAddToSelected = (
        tileID: ICompareTile["product_compare_tile"]["id"],
    ) => {
        dispatch(addToSelected({ tileID }));
    };

    const handleRemoveFromSelected = (
        tileID: ICompareTile["product_compare_tile"]["id"],
    ) => {
        dispatch(removeFromSelected({ tileID }));
    };

    const handleShowModal = () => {
        dispatch(showModal());
    };

    return (
        <>
            {showCompareFunctionality && (
                <SelectedProductTray
                    tiles={tiles}
                    productComparePageURL={props.productComparePageURL}
                    ctaText={props.ctaText}
                />
            )}
            <ProductSelectionGridComponent
                header={props.header}
                tiles={tiles}
                selectedTileIDs={selectedTileIDs}
                priceSelectValue={priceSelectValue}
                financingLink={props.financingLink}
                addToSelected={handleAddToSelected}
                removeFromSelected={handleRemoveFromSelected}
                showStartingAt={props.showStartingAt}
                showRating={props.showRating}
                ctaText={props.ctaText}
                showModal={handleShowModal}
                productComparePageURL={props.productComparePageURL}
                preselectedSize={props.preselectedSize || null}
                sizeAttributeOptionGroup={props.sizeAttributeOptionGroup}
                theme={props.theme}
                footer={props.footer}
                groupHeader={props.groupHeader}
                showSizeSelector={props.showSizeSelector}
            />
        </>
    );
};
